import React from 'react';
import {
  ArticleContent,
  ArticleMainImage,
  ArticleTitle,
  BreadCrumbs,
  ContentWithoutSidebar,
  FullWidthPostMainContent,
  LARGE_SCREEN_SIZE,
  OpenWeb,
  PostContentWithAside,
  RecommendationsAdvertisement,
  ShareButton,
  Slideshow,
  Sponsor,
  SwitchableShareMenu,
} from 'mm-ui-components';
import {
  ArticleComponentDataProps,
  FiniteScrollArticleComponentDataProps,
  MainArticleComponentDataProps,
} from '../utils/articleComponent.utils';
import { createBlocksWidth } from '../postPage.util';
import { useFiniteScrollBrowserURLManagerRegistration } from '../../components/finiteScroll/FiniteScrollBrowserURLManager';
import { DisclaimersAtDynamicPosition } from '../../components/disclaimer/DisclaimersAtDynamicPosition';

const defaultBlocksWidths = createBlocksWidth(740, 730, 640);

const overrideBlocksWidths = {
  image: createBlocksWidth(760, 600, 660),
};
const coverWidth = createBlocksWidth(1080, 730, 640);
const openWebMargins = {
  left: 5,
  right: 0,
};
const breadCrumbsMarginFactor = {
  largeMarginFactor: 1,
  mediumMarginFactor: 1.5,
  smallMarginFactor: 1,
};

const getOutbrainWidgetIDForTest = (widgetID: string, experiments?: string[]) => {
  if (experiments?.includes('outbrain-widget|ar-8')) return 'AR_8';
  if (experiments?.includes('outbrain-widget|ar-9')) return 'AR_9';
  return widgetID;
};

export const OdesaArticleComponent: React.FunctionComponent<ArticleComponentDataProps> = props => {
  const {
    breadCrumbs,
    title,
    intro,
    by,
    updatedText,
    updatedAt,
    updatedAtISO,
    showUpdatedAt,
    authors,
    shareConfig,
    cover,
    siteName,
    templateName,
    sponsor,
    slideshow,
    body,
    sideBar1Id,
    sideBar2Id,
    showRecommendationsAdvertisement,
    outbrainWidgetID,
    createdAt,
    createdAtISO,
    tags,
    md5hashPostCanonicalUrl,
    openWebId,
    sideBar2AdOnSuccess,
    sideBar1AdOnSuccess,
    mainArticleURL,
    disclaimerText,
    reviewDisclaimerText,
    shouldDisplayDisclaimerAtBottom,
    commercialTags,
    shouldShowHeroImage = true,
    resourceID,
    ownerUsername,
  } = props;

  const { experiments, canonical, addMMPlayerPlaceholder = false, addOpenwebLiveblogPlaceholder = false } = props as MainArticleComponentDataProps;
  const { articleUrl } = props as FiniteScrollArticleComponentDataProps;

  // Outbrain Widget Experiment
  const disableOutbrainForTest = experiments?.includes('outbrain-widget|no-outbrain');
  const outbrainWidgetIDForTest = getOutbrainWidgetIDForTest(outbrainWidgetID, experiments);

  const url = mainArticleURL || canonical || articleUrl;
  const ref = useFiniteScrollBrowserURLManagerRegistration(url as string, title);
  return (
    <div ref={ref}>
      <ContentWithoutSidebar maxWidth={LARGE_SCREEN_SIZE}>
        <FullWidthPostMainContent>
          <header>
            <ArticleTitle
              title={title}
              by={by}
              updatedText={updatedText}
              createdAt={createdAt}
              createdAtISO={createdAtISO}
              updatedAt={updatedAt}
              updatedAtISO={updatedAtISO}
              showUpdatedAt={showUpdatedAt}
              authors={authors}
              intro={intro}
            >
              <ShareButton shareConfig={shareConfig} canonical={url} smallScreenOnly />
            </ArticleTitle>
            {shouldShowHeroImage && (
              <ArticleMainImage
                {...cover}
                siteName={siteName}
                templateName={templateName}
                defaultBlocksWidths={coverWidth}
                paddingOverride={{ largePadding: 1, mediumPadding: 0, smallPadding: 0 }}
              />
            )}
          </header>
          {sponsor ? <Sponsor {...sponsor} /> : null}
          <SwitchableShareMenu shareConfig={shareConfig} canonical={url} hideOnSmallScreen />
          <PostContentWithAside adId1={sideBar1Id} adId2={sideBar2Id} adOnSuccess1={sideBar1AdOnSuccess} adOnSuccess2={sideBar2AdOnSuccess}>
            <Slideshow {...slideshow} experiments={experiments} resourceID={resourceID} commercialTags={commercialTags} ownerUsername={ownerUsername} blockWidths={defaultBlocksWidths}>
              <DisclaimersAtDynamicPosition
                disclaimerText={disclaimerText}
                reviewDisclaimerText={reviewDisclaimerText}
                defaultBlocksWidths={defaultBlocksWidths}
                shouldDisplayDisclaimerAtBottom={shouldDisplayDisclaimerAtBottom}
              >
                <ArticleContent
                  body={body}
                  templateName={templateName}
                  siteName={siteName}
                  defaultBlocksWidths={defaultBlocksWidths}
                  overrideBlocksWidths={overrideBlocksWidths}
                  addMMPlayerPlaceholder={addMMPlayerPlaceholder}
                  addOpenwebLiveblogPlaceholder={addOpenwebLiveblogPlaceholder}
                  imageWidthSmall={360}
                  imageWidthMedium={720}
                  imageWidthLarge={720}
                  totalSlides={slideshow.totalSlides}
                  experiments={experiments}
                  commercialTags={commercialTags}
                  slidesLinks={slideshow.slidesLinks}
                  resourceID={resourceID}
                  ownerUsername={ownerUsername}
                />
              </DisclaimersAtDynamicPosition>
            </Slideshow>
            {breadCrumbs ? (
              <BreadCrumbs
                {...breadCrumbs}
                marginFactor={breadCrumbsMarginFactor}
                isBottom
              />
            ) : null}
          </PostContentWithAside>
        </FullWidthPostMainContent>
        {showRecommendationsAdvertisement && !!outbrainWidgetID && !disableOutbrainForTest
            ? <RecommendationsAdvertisement experiment={experiments?.join()} outbrainWidgetID={outbrainWidgetIDForTest} pageLiveUrl={url} /> : null}
        {openWebId && md5hashPostCanonicalUrl
        && (
          <OpenWeb
            spotId={openWebId}
            postCanonicalUrl={url}
            tags={tags}
            largeScreenMarginFactors={openWebMargins}
            md5hashPostCanonicalUrl={md5hashPostCanonicalUrl}
          />
        )}
      </ContentWithoutSidebar>
    </div>
  );
};
